import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'
import { Controller } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { FormControl, InputLabel, TextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'

interface Props {
  name: string
  labelPosition?: 'top' | 'left'
  control: any
  countryStates: CountryStates
  disabled: boolean
  required?: boolean
  value: SelectOption[] | SelectOption | null
  multiple?: boolean
  courseEditor?: boolean
}

export default function CountryStateSelect({
  name,
  labelPosition = 'top',
  control,
  countryStates,
  disabled,
  required = false,
  value,
  multiple = false,
  courseEditor,
}: Props): JSX.Element {
  const [selected, setSelected] = useState<SelectOption[] | SelectOption | null>([])
  const intl = useIntl()
  const { states, status } = countryStates

  useEffect(() => {
    const isArray = Array.isArray(value)
    const notEmptyMultipleInitialValue = multiple && isArray && Array.isArray(selected) && selected.length === 0
    const notEmptySingleInitialValue =
      !multiple && value && (!selected || (Array.isArray(selected) && selected.length === 0))
    const emptyInitialValue = (Array.isArray(value) && value.length === 0) || value === null
    if (notEmptyMultipleInitialValue || notEmptySingleInitialValue || emptyInitialValue) {
      setSelected(value)
    }
  }, [value, selected, multiple])

  const getPlaceholderText = () => {
    if (!disabled && required) {
      return intl.formatMessage({ id: 'strings.required' })
    }
    return disabled ? '' : intl.formatMessage({ id: 'strings.search' })
  }

  const getValues = () => {
    return states && selected ? selected : null
  }

  const handleChange = (newValues: SelectOption[] | SelectOption | null) => {
    setSelected(newValues)
    return newValues
  }

  const getOptions = (): SelectOption[] => {
    return states.map((state: CountryState) => {
      const { id, stateName, stateCountry } = state
      return { key: id, label: `${stateCountry} - ${stateName}` }
    })
  }

  const FieldLabel = disabled ? DisabledLabel : ActiveLabel
  const ContainerVariant = labelPosition === 'left' ? LabelInputRowContainer : FormControl

  return (
    <>
      <ContainerVariant margin="normal" fullWidth disabled={disabled} required={required}>
        {!courseEditor && (
          <FieldLabel>
            <FormattedMessage id="strings.state" />
          </FieldLabel>
        )}
        <Controller
          control={control}
          name={name}
          defaultValue={getValues()}
          render={({ field: { onChange } }) => (
            <Autocomplete
              loading={status === 'loading'}
              disabled={disabled}
              disableListWrap
              onChange={(event, newValues) => onChange(handleChange(newValues))}
              options={getOptions()}
              getOptionLabel={(option) => option.label || ''}
              autoHighlight
              value={getValues()}
              multiple={multiple}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={getPlaceholderText()}
                  required={required}
                  label={courseEditor ? <FormattedMessage id="strings.state" /> : undefined}
                  InputLabelProps={{ style: { color: courseEditor ? '#373D40' : '#1AA85D', fontSize: 14 } }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.key === value.key}
            />
          )}
        />
      </ContainerVariant>
    </>
  )
}

const LabelInputRowContainer = styled(FormControl)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  margin: 5px 0 30px;

  & > label {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    position: relative;
    height: 40px;
    width: 100px;
    margin: 0 10px 0 0;
  }
  & > div {
    flex: 1;
  }
`

const ActiveLabel = styled(InputLabel)`
  padding: 0 10px;
  margin-top: -20px;
  transition: none;
  transform: none;
  & .MuiFormLabel-asterisk {
    display: none;
  }
`

const DisabledLabel = styled(ActiveLabel)`
  color: ${(props) => props.theme.customPalette.mediumGray} !important;
`
